// Recommendations screen: ranked driver-based actions per scope, with region/group/worker
// breakdowns. Port of the prototype's renderRecommendations.

import React from 'react';
import { cn } from '@/lib/utils';
import { useDash } from '../state';
import { Banner, Chip, Dot, Panel, SecHeader } from '../ui';
import type { Tone } from '../ui';
import { GROUPS, REGIONS, WORKERS, readiness, wArr, workersInGroup, workersInRegion } from '../engine/data';
import type { Worker } from '../engine/data';
import { rdyTone } from '../engine/scores';
import { analyseDrivers } from '../engine/insights';
import type { InsightOpts } from '../engine/insights';

type Step = ReturnType<typeof analyseDrivers>[number];

// Priority 1/2/3+ edge colour on each step card.
const PRIORITY_EDGE = ['border-l-destructive', 'border-l-warning', 'border-l-info', 'border-l-info'];

const Steps: React.FC<{ steps: Step[] }> = ({ steps }) => (
  <div className='flex flex-col gap-2.5'>
    {steps.map((s, i) => (
      <div
        key={`${s.key}-${s.action}`}
        className={cn(
          'flex gap-3.5 rounded-md border border-l-2 border-border bg-card p-3',
          PRIORITY_EDGE[i] ?? PRIORITY_EDGE[3],
        )}
      >
        <div className='flex w-20 shrink-0 flex-col items-center gap-1 text-center'>
          <s.icon className='h-5 w-5' style={{ color: s.color }} />
          <div className='text-[10px] font-semibold uppercase tracking-wider' style={{ color: s.color }}>
            {s.label}
          </div>
        </div>
        <div className='min-w-0'>
          <div className='text-[13px] font-semibold'>{s.action}</div>
          <div className='mt-0.5 text-xs text-muted-foreground'>{s.detail}</div>
        </div>
      </div>
    ))}
  </div>
);

/** A scope section with a coloured header bar. Renders nothing without workers or drivers. */
const RecsSection: React.FC<{
  label: React.ReactNode;
  color: string;
  workers: Worker[];
  opts: InsightOpts;
}> = ({ label, color, workers, opts }) => {
  if (!workers.length) return null;
  const steps = analyseDrivers(workers, opts);
  if (!steps.length) return null;
  return (
    <div className='mb-6'>
      <div className='mb-3 flex items-center gap-2.5 border-b border-border pb-2.5'>
        <div className='h-7 w-1 shrink-0 rounded-sm' style={{ background: color }} />
        <div>
          <div className='text-[15px] font-bold'>{label}</div>
          <div className='text-[11px] text-muted-foreground'>
            {workers.length} worker{workers.length !== 1 ? 's' : ''} · Primary driver:{' '}
            <span className='font-semibold' style={{ color: steps[0].color }}>
              {steps[0].label}
            </span>
          </div>
        </div>
      </div>
      <Steps steps={steps} />
    </div>
  );
};

/** Region switcher, coloured per region. */
const RegionButtons: React.FC<{ activeRid: string | null; onPick: (rid: string) => void; small?: boolean }> = ({
  activeRid,
  onPick,
  small,
}) => (
  <div className={cn('flex flex-wrap gap-2', small ? 'mb-2' : 'mb-4')}>
    {Object.entries(REGIONS).map(([rid, r]) => {
      const active = rid === activeRid;
      return (
        <button
          key={rid}
          type='button'
          onClick={() => onPick(rid)}
          className={cn(
            'rounded-md border font-semibold transition-colors',
            small ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]',
            !active && 'border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
          style={active ? { borderColor: r.color, background: `${r.color}22`, color: r.color } : undefined}
        >
          {r.name}
        </button>
      );
    })}
  </div>
);

const Empty: React.FC = () => <Banner tone='info'>No data available for recommendations.</Banner>;

export const RecommendationsScreen: React.FC = () => {
  const d = useDash();
  const sc = d.drillScope;

  if (sc === 'company') {
    const allW = wArr();
    const topSteps = analyseDrivers(allW, { isCompany: true, scopeName: 'the company' });
    const topDriver = topSteps[0];
    return (
      <div>
        <Panel
          className='mb-6'
          title='Company-wide priority actions'
          sub={`${allW.length} workers · ${Object.keys(REGIONS).length} regions`}
        >
          {topDriver && (
            <div className='mb-3.5 rounded-lg border border-border bg-muted/40 px-2.5 py-2 text-xs text-muted-foreground'>
              <span className='font-semibold text-warning'>Dominant fatigue driver across the organisation:</span>{' '}
              {topDriver.label} — {topSteps.map((s) => s.label).join(' · ')}
            </div>
          )}
          <Steps steps={topSteps} />
        </Panel>
        <SecHeader>Region-level breakdown</SecHeader>
        {Object.entries(REGIONS).map(([rid, reg]) => (
          <RecsSection
            key={rid}
            label={reg.name}
            color={reg.color}
            workers={workersInRegion(rid)}
            opts={{ regionId: rid, scopeName: reg.name }}
          />
        ))}
      </div>
    );
  }

  if (sc === 'region' && d.activeRegion) {
    const rid0 = d.activeRegion;
    const reg = REGIONS[rid0];
    return (
      <div>
        <RegionButtons activeRid={rid0} onPick={(rid) => d.patch({ activeRegion: rid })} />
        <RecsSection
          label={`${reg.name} — All Groups`}
          color={reg.color}
          workers={workersInRegion(rid0)}
          opts={{ regionId: rid0, scopeName: reg.name }}
        />
        <SecHeader>Group-level breakdown</SecHeader>
        {reg.groups.map((gid) => (
          <RecsSection
            key={gid}
            label={GROUPS[gid].name}
            color={reg.color}
            workers={workersInGroup(gid)}
            opts={{ scopeName: GROUPS[gid].name }}
          />
        ))}
      </div>
    );
  }

  if (sc === 'group' && d.activeGroup !== null) {
    const gid0 = d.activeGroup;
    const grpRid = GROUPS[gid0].region;
    const reg = REGIONS[grpRid];
    const gw = workersInGroup(gid0);
    return (
      <div>
        <RegionButtons
          small
          activeRid={grpRid}
          onPick={(rid) => d.patch({ activeRegion: rid, activeGroup: REGIONS[rid].groups[0] })}
        />
        <div className='mb-4 flex flex-wrap gap-1.5'>
          {reg.groups.map((gid) => (
            <Chip key={gid} active={gid === gid0} onClick={() => d.patch({ activeGroup: gid })}>
              {GROUPS[gid].name}
            </Chip>
          ))}
        </div>
        <RecsSection label={GROUPS[gid0].name} color={reg.color} workers={gw} opts={{ scopeName: GROUPS[gid0].name }} />
        <SecHeader>Individual recommendations — {GROUPS[gid0].name}</SecHeader>
        {[...gw]
          .sort((a, b) => b.fed - a.fed)
          .map((w) => (
            <RecsSection
              key={w.init}
              label={
                <>
                  {w.name} <span className='text-[11px] font-normal text-muted-foreground'>({w.role})</span>
                </>
              }
              color='var(--color-success)'
              workers={[w]}
              opts={{ scopeName: w.name }}
            />
          ))}
      </div>
    );
  }

  if (sc === 'individual') {
    const pool =
      d.activeGroup !== null
        ? workersInGroup(d.activeGroup)
        : d.activeRegion
          ? workersInRegion(d.activeRegion)
          : wArr();
    const sorted = [...pool].sort((a, b) => b.fed - a.fed);
    const w = d.activeWorker ? WORKERS[d.activeWorker] : undefined;
    return (
      <div>
        <div className='mb-4 flex flex-wrap items-center gap-1.5 border-b border-border pb-3'>
          <span className='mr-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'>
            Individual
          </span>
          {sorted.map((p) => {
            const tone: Tone = rdyTone(readiness(p));
            return (
              <Chip key={p.init} active={p.init === d.activeWorker} onClick={() => d.patch({ activeWorker: p.init })}>
                <span className='inline-flex items-center gap-1.5'>
                  <Dot tone={tone} className='h-1.5 w-1.5' />
                  {p.init} {p.name.split(' ')[0]}
                </span>
              </Chip>
            );
          })}
        </div>
        {w ? (
          <RecsSection
            label={`${w.name} — Personal recommendations`}
            color='var(--color-success)'
            workers={[w]}
            opts={{ scopeName: w.name }}
          />
        ) : (
          <Empty />
        )}
      </div>
    );
  }

  return <Empty />;
};
